import { ApiBQTable, ApiDisplay, ApiAttribute } from "./api_data";
import { BQData, PivotMeasure } from "./app";
import { Vertbars } from "../../shared/vertbars";
import { GraphValue } from "../../shared/graph";
import { BQ } from "./bq";
import { Editor } from "./editor";
import { cloneDeep } from 'lodash';
import { SingleNumber } from "../../shared/single_number";
import { Linegraph, LinegraphOpts } from "../../shared/linegraph";
import { SVG } from "../../shared/svg";
import { Size } from "../../shared/size";

export interface GraphData {
  data: GraphValue[][],
  bucket_labels: string[],
}

export interface DataConnection {
  viz_id: string,
  id: string,
  data: BQData,
  position: number,
}

export interface TableConnection {
  viz_id: string,
  id: string,
  table: ApiBQTable,
  closed: boolean,
}

export interface FilterConnection {
  viz_id: string;
  id: string;
  column: string;
  date_mode: string;
  date_min: string | null;
  date_max: string | null;
  date_current_interval: string;
  date_recent_interval: string;
  date_recent_count: number;
  text_mode: string;
  text_query: string | null;
}

export interface AttributeApplyResult {
  pivot_op: string | null;
  pivot_facet: string | null;
}

export class Display {

  id: string;
  owner_id: string;
  title: string;
  version: number;
  created_at: string;

  selected_table: TableConnection | null = null;
  selected_columns: DataConnection[] = [];
  group_by: string | null = null;
  group_by_interval: string | null = null;
  sort: string | null = null;
  sort_order: string | null = null;
  query_columns: BQData[] = [];
  data: BQData[] = [];
  filters: FilterConnection[] = [];

  count_target: string | null = null;
  count: BQData = {
    project_id: "",
    dataset_id: "",
    table_id: "",
    column_name: "Count",
    column_type: "count",
    rows: [],
    pivoted: false,
    pivot_facet: null,
    pivot: null,
    pivot_op: null,
    pivot_target: null,
    pivot_measures: [],

    operation: null,
    loading: false,
    hidden: false,
    closed: false,
  }

  type = "bars";
  aspect_ratio: number = 400 / 314;
  line_curve: number = 0;
  line_style: string = "line";
  closed: boolean = false;
  data_changes = false;

  constructor(id: string, owner_id: string, title: string, version: number, created_at: string) {
    this.id = id;
    this.owner_id = owner_id;
    this.title = title;
    this.version = version;
    this.created_at = created_at;
  }

  static render(viz: Display, g: SVGGElement, inset: number): Size {
    g.innerHTML = "";

    const shadow_size = 7;
    const bg = SVG.rect(shadow_size, shadow_size, (400 - (shadow_size * 2)), 180);
    bg.setAttribute("filter", "url(#shadow)");
    bg.setAttribute("rx", "10");
    bg.setAttribute("fill", "white");
    bg.classList.add("graph-panel");
    g.appendChild(bg);

    let size: Size = {
      width: 400,
      height: 180,
    }

    switch (viz.type) {
      case "lines":
        const data = Display.graph_data(viz.data);
        const opts: LinegraphOpts = {
          render_key: true,
          inset: inset,
          color: "#00c0f3",
          color_set: null,
          curve: viz.line_curve,
          style: viz.line_style,
          render_title: true,
        }
        size = Linegraph.render(g, viz.id, viz.title, data.data, data.bucket_labels, opts, (400 / 180));
        break;

      case "number":
        const num = Display.graph_number(viz.data);
        size = SingleNumber.render(g, viz.id, viz.title, num, "#00c0f3", inset);
        break;

      default:
        const vert_data = Display.graph_data(viz.data);
        size = Vertbars.render(g, viz.id, viz.title, vert_data.data, vert_data.bucket_labels, "#00c0f3", inset);
        break;
    }

    bg.setAttribute("width", size.width - (shadow_size * 2) + "px");
    bg.setAttribute("height", size.height - (shadow_size * 2) + "px");
    return size;
  }

  private static apply_conn_attr(conn: DataConnection, attr: ApiAttribute): AttributeApplyResult {
    const res: AttributeApplyResult = {
      pivot_op: null,
      pivot_facet: null,
    }
    if (attr.parent !== conn.id) {
      return res;
    }
    switch (attr.key) {

      case "pivoted":
        if (attr.number) {
          conn.data.pivoted = true;
        }
        break;

      case "pivot_facet":
        conn.data.pivot_facet = attr.text;
        res.pivot_facet = attr.text;
        break;

      case "pivot_operation":
        conn.data.pivot_op = attr.text;
        res.pivot_op = attr.text;
        break;

      case "operation":
        conn.data.operation = attr.text;
        break;

      case "closed":
        if (attr.number) {
          conn.data.closed = true;
        }
        break;

      case "position":
        if (attr.number) {
          conn.position = attr.number;
        }
        break;

      default:
        break;
    }
    return res;
  }

  static load_columns(viz: Display, api_viz: ApiDisplay) {
    if (!viz.selected_table || !viz.selected_table.table.columns) {
      return;
    }
    viz.selected_columns = [];
    for (const conn of api_viz.connections) {
      if (conn.key !== "column") {
        continue;
      }
      for (const col of viz.selected_table.table.columns) {
        if (col.name !== conn.child) {
          continue;
        }
        const data: BQData = {
          project_id: viz.selected_table.table.project_id,
          dataset_id: viz.selected_table.table.dataset_id,
          table_id: col.table_id,
          column_name: col.name,
          column_type: col.type,
          rows: [],
          pivoted: false,
          pivot_facet: null,
          pivot: null,
          pivot_op: null,
          pivot_target: null,
          pivot_measures: [],
          operation: null,
          loading: false,
          hidden: false,
          closed: false,
        }
        const c: DataConnection = {
          viz_id: viz.id,
          id: conn.id,
          data: data,
          position: 0,
        }
        viz.selected_columns.push(c);
        break;
      }
    }

    const columns: DataConnection[] = [];
    for (const conn of viz.selected_columns) {
      columns.push(conn);
      let pivot_op: string | null = null;
      let pivot_facet: string | null = null;
      for (const attr of api_viz.attributes) {
        const res = Display.apply_conn_attr(conn, attr);
        if (res.pivot_op) {
          pivot_op = res.pivot_op;
        }
        if (res.pivot_facet) {
          pivot_facet = res.pivot_facet;
        }
      }

      for (const c of api_viz.connections) {
        if (c.parent !== conn.id) {
          continue;
        }
        if (c.key === "pivot_value") {
          const p_data: BQData = {
            project_id: viz.selected_table.table.project_id,
            dataset_id: viz.selected_table.table.dataset_id,
            table_id: viz.selected_table.table.id,
            column_name: conn.data.column_name,
            column_type: conn.data.column_type,
            rows: [],
            pivoted: false,
            pivot_facet: pivot_facet,
            pivot: c.child,
            pivot_op: pivot_op,
            pivot_target: null,
            pivot_measures: [],
            operation: null,
            loading: false,
            hidden: false,
            closed: false,
          }
          const p_conn: DataConnection = {
            viz_id: viz.id,
            id: c.id,
            data: p_data,
            position: 0,
          }
          columns.push(p_conn);
          for (const attr of api_viz.attributes) {
            Display.apply_conn_attr(p_conn, attr);
          }
        }
      }
    }
    columns.sort(function (a, b) {
      return a.position - b.position;
    });
    viz.selected_columns = columns;
    for (const col of viz.selected_columns) {
      for (const conn of api_viz.connections) {
        if (conn.parent !== col.id) {
          continue;
        }
        if (conn.key === "pivot_measure") {
          const measure: PivotMeasure = {
            id: conn.id,
            column: conn.child,
          }
          col.data.pivot_measures.push(measure);
        }
      }
      for (const attr of api_viz.attributes) {
        if (attr.parent !== col.id) {
          continue;
        }
        if (attr.key === "hidden" && attr.number) {
          col.data.hidden = true;
        }
      }
    }
  }

  private static apply_table_attr(conn: TableConnection | null, attr: ApiAttribute) {
    if (!conn || attr.parent !== conn.id) {
      return;
    }
    switch (attr.key) {

      case "closed":
        if (attr.number) {
          conn.closed = true;
        }
        break;

      default:
        break;
    }
  }

  private static apply_attr(viz: Display, attr: ApiAttribute) {
    if (attr.parent !== viz.id) {
      return;
    }
    switch (attr.key) {
      case "type":
        if (attr.text) {
          viz.type = attr.text;
        }
        break;

      case "aspect_ratio":
        if (attr.number) {
          viz.aspect_ratio = attr.number;
        }
        break;

      case "line_curve":
        if (attr.number) {
          viz.line_curve = attr.number;
        }
        break;

      case "line_style":
        if (attr.text) {
          viz.line_style = attr.text;
        }
        break;

      case "closed":
        if (attr.number) {
          viz.closed = true;
        }
        break;

      case "group_by":
        viz.group_by = attr.text;
        break;

      case "group_by_interval":
        viz.group_by_interval = attr.text;
        break;

      case "sort":
        viz.sort = attr.text;
        break;

      case "sort_order":
        viz.sort_order = attr.text;
        break;

      case "count_hidden":
        if (attr.number) {
          viz.count.hidden = true;
        }
        break;

      case "count":
        viz.count_target = attr.text;
        break;

      default:
        break;
    }
  }

  static load_filters(viz: Display, api_viz: ApiDisplay) {
    viz.filters = [];
    for (const conn of api_viz.connections) {
      if (conn.key !== "filter") {
        continue;
      }
      const f: FilterConnection = {
        id: conn.id,
        viz_id: viz.id,
        column: "",
        date_mode: "",
        date_current_interval: "",
        date_recent_interval: "",
        date_recent_count: 0,
        date_min: null,
        date_max: null,
        text_mode: "",
        text_query: "",
      }

      for (const attr of api_viz.attributes) {
        if (attr.parent !== f.id) {
          continue;
        }
        switch (attr.key) {
          case "column":
            if (attr.text) {
              f.column = attr.text;
            }
            break;

          case "date_mode":
            if (attr.text) {
              f.date_mode = attr.text;
            }
            break;

          case "date_current_interval":
            if (attr.text) {
              f.date_current_interval = attr.text;
            }
            break;

          case "date_recent_interval":
            if (attr.text) {
              f.date_recent_interval = attr.text;
            }
            break;

          case "date_recent_count":
            if (attr.number) {
              f.date_recent_count = attr.number;
            }
            break;

          case "date_min":
            f.date_min = attr.text;
            break;

          case "date_max":
            f.date_max = attr.text;
            break;

          case "text_mode":
            if (attr.text) {
              f.text_mode = attr.text;
            }
            break;

          case "text_query":
            f.text_query = attr.text;
            break;

          default:
            break;
        }
      }
      if (f.column !== "") {
        viz.filters.push(f);
      }
    }
  }

  static parse(api_viz: ApiDisplay, tables: ApiBQTable[]): Display {
    const viz = new Display(api_viz.id, api_viz.owner_id, api_viz.title, api_viz.version, api_viz.created_at);
    for (const conn of api_viz.connections) {
      if (conn.parent === viz.id && conn.key === "table") {
        for (const t of tables) {
          if (t.id === conn.child) {
            const c: TableConnection = {
              viz_id: viz.id,
              id: conn.id,
              table: t,
              closed: false,
            }
            viz.selected_table = c;
            break;
          }
        }
      }
    }
    for (const attr of api_viz.attributes) {
      Display.apply_attr(viz, attr);
      Display.apply_table_attr(viz.selected_table, attr);
    }
    Display.load_columns(viz, api_viz);
    Display.load_filters(viz, api_viz);
    Display.build_query_columns(viz);
    return viz;
  }

  static graph_data(bq_data: BQData[]): GraphData {
    let row_count = 0;
    let number_object_idxs = [];
    let label_object_idx = -1;
    let idx = 0;
    for (const col of bq_data) {
      row_count = col.rows.length;

      if (BQ.numeric_col(col.column_type) || col.pivot || col.column_type === "count") {
        number_object_idxs.push(idx);
      }
      if (label_object_idx < 0 && BQ.date_col(col.column_type)) {
        label_object_idx = idx;
      }
      idx++;
    }

    const data: GraphValue[][] = [];
    const bucket_labels: string[] = [];
    for (let i = 0; i < row_count; i++) {
      const vals: GraphValue[] = [];

      let label = "";
      if (label_object_idx > -1) {
        const bucket = bq_data[label_object_idx].rows[i];
        bucket_labels.push(bucket);
      }
      if (number_object_idxs.length === 0) {
        const graph_val: GraphValue = {
          amount: 0,
          label: label,
        }
        vals.push(graph_val);
      } else {
        for (const idx of number_object_idxs) {
          const v = bq_data[idx].rows[i];
          let l = bq_data[idx].column_name;
          const p = bq_data[idx].pivot;
          if (p != null) {
            l = p;
          }
          const g_val: GraphValue = {
            amount: v,
            label: l,
          }
          vals.push(g_val);
        }
      }
      data.push(vals);
    }

    return {
      data: data,
      bucket_labels: bucket_labels,
    }
  }

  static graph_number(bq_data: BQData[]): number | null {
    for (const col of bq_data) {
      if ((BQ.numeric_col(col.column_type) || col.pivot || col.column_type === "count") && col.rows.length > 0) {
        const num = col.rows[0] as number;
        return num;
      }
    }
    return null;
  }

  static build_query_columns(viz: Display) {

    // copy pivot measures to sub columns
    for (const sel of viz.selected_columns) {
      if (!sel.data.pivoted) {
        continue;
      }
      const key = Editor.data_key(sel.data, false);
      for (const s of viz.selected_columns) {
        if (s.data.pivoted) {
          continue;
        }
        const k = Editor.column_key(s.data.dataset_id, s.data.table_id, s.data.column_name, null);
        if (k === key) {
          s.data.pivot_measures = sel.data.pivot_measures;
        }
      }
    }

    const cols: BQData[] = [];
    for (const sel of viz.selected_columns) {
      if (BQ.skip_col(sel.data)) {
        continue;
      }
      if (sel.data.pivot_measures.length === 0 || sel.data.pivot_op === null) {
        cols.push(sel.data);
      } else {
        for (const target of sel.data.pivot_measures) {
          const t = cloneDeep(sel);
          t.data.pivot_target = target.column;
          cols.push(t.data);
        }
      }
    }

    if (viz.count.hidden === false) {
      cols.push(viz.count);
    }
    viz.query_columns = cols;
  }
}